import { supabase } from "./supabaseClient";

// Strip heavy raw rows from the profile before saving — same as the original
// localStorage path. Aggregated profile data is kept.
function lightenProfile(profile) {
  if (!profile) return null;
  return { ...profile, rawRows: [] };
}

// Build the project_data blob the DB column stores.
function buildProjectData({ site, assumptions, diesel, profile, results, irrData, irrMeta }) {
  return {
    site, assumptions, diesel,
    profile: lightenProfile(profile),
    results, irrData, irrMeta,
  };
}

// List all projects for the signed-in user, newest first.
// Annotates each row with `revision` (1-based, oldest = 1, newest = N) per project_name,
// computed from the existing revision column when available.
export async function listProjects() {
  const { data, error } = await supabase
    .from("projects")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

// Save current state as a new row. Auto-increments `revision` for the same
// project_name within this user's rows.
export async function saveProject({
  name, site, assumptions, diesel, profile, results, irrData, irrMeta,
}) {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr) throw userErr;
  const user = userData?.user;
  if (!user) throw new Error("Not signed in.");

  const projectName = (name || "").trim() || (
    site?.clientName
      ? `${site.clientName} — ${new Date().toLocaleDateString("en-GB")}`
      : `Project ${new Date().toLocaleDateString("en-GB")}`
  );

  // Find the highest existing revision for this project_name (this user only).
  const { data: prior, error: priorErr } = await supabase
    .from("projects")
    .select("revision")
    .eq("user_id", user.id)
    .eq("project_name", projectName)
    .order("revision", { ascending: false })
    .limit(1);
  if (priorErr) throw priorErr;
  const nextRevision = (prior?.[0]?.revision || 0) + 1;

  const row = {
    user_id:      user.id,
    project_name: projectName,
    client_name:  site?.clientName || null,
    site_name:    site?.location   || null,
    revision:     nextRevision,
    platform_type: "CI",
    project_data: buildProjectData({ site, assumptions, diesel, profile, results, irrData, irrMeta }),
  };

  const { data, error } = await supabase
    .from("projects")
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteProject(id) {
  const { error } = await supabase.from("projects").delete().eq("id", id);
  if (error) throw error;
}
